// ============================================================
// ANSICHTSSACHE-AGENT – „Weitere zeigen" am Fuss einer Liste
// ============================================================
// Die Zeile, die am Ende des Ausschnitts steht, wenn eine Liste
// nicht alles zeichnet. Sie sagt ZWEI Dinge in einem Satz: wie
// viele gerade zu sehen sind und wie viele es wirklich sind.
// Eine Liste, die schweigend nach 200 Zeilen aufhoert, ist eine
// Falle — man haelt das Gesehene fuer den ganzen Bestand.
//
// Der Weg weiter ist ein VERWEIS, kein Knopf mit Zustand: der
// Umfang steht in der Adresse (`lib/sichtfenster.ts`), also ist
// er teilbar, ueberlebt das Neuladen und braucht keine Zeile
// JavaScript. `scroll={false}`, damit man nach dem Nachladen
// dort steht, wo man stehen geblieben ist.
//
// ZWEI FASSUNGEN, ein Inhalt: `WeitereZeile` fuer eine Tabelle
// (sie bringt `tbody`/`tr`/`td` mit, weil dort nichts anderes
// zwischen den Zeilen und der Summenzeile stehen darf),
// `Weitere` fuer eine Liste ohne Tabelle. Wer die Zeile in jeder
// Liste von Hand um ein `td colSpan` wickelt, schreibt zehnmal
// dasselbe und beim elften Mal etwas anderes.
// ============================================================

import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { formatDecimal } from "@/lib/number-format";
import { cn } from "@/lib/utils";
import { UMFANG_STUFE } from "@/lib/sichtfenster";

export function Weitere({
  gezeigt,
  gesamt,
  href,
  /**
   * Wie die Dinge heissen, die hier gezaehlt werden — Einzahl und
   * Mehrzahl im DATIV: der Satz lautet „200 von 1.476 Umsaetzen".
   */
  wort = ["Zeile", "Zeilen"],
  className,
}: {
  gezeigt: number;
  gesamt: number;
  href: string;
  wort?: [string, string];
  className?: string;
}) {
  const offen = gesamt - gezeigt;
  const naechste = Math.min(offen, UMFANG_STUFE);

  return (
    <div
      className={cn(
        "bg-bg-secondary border-border-subtle flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t px-5 py-4",
        className,
      )}
    >
      <span className="text-caption text-text-tertiary">
        {formatDecimal(gezeigt)} von {formatDecimal(gesamt)} {gesamt === 1 ? wort[0] : wort[1]}{" "}
        gezeigt
      </span>
      <Link
        href={href}
        scroll={false}
        className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
      >
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        Weitere {formatDecimal(naechste)} zeigen
      </Link>
    </div>
  );
}

/**
 * Dieselbe Zeile, eingehaengt in eine Tabelle.
 *
 * `fenster` ist, was `sichtfenster()` zurueckgibt — steht darin
 * `weiter: null`, zeichnet das Bauteil NICHTS. Damit ist der
 * Aufruf in der Liste eine Zeile ohne Bedingung davor.
 */
export function WeitereZeile({
  fenster,
  spalten,
  href,
  wort,
}: {
  fenster: { gezeigt: unknown[]; gesamt: number; weiter: number | null };
  spalten: number;
  /** Die Adresse fuer den naechsten Umfang — `weiterHref(...)`. */
  href: string;
  wort?: [string, string];
}) {
  if (fenster.weiter === null) return null;

  return (
    // `data-weitere`: in der `Listentabelle` gehoert dieser Rumpf
    // nicht zur Karte — er steht darunter auf dem Grund, wie
    // „Weitere" unter dem Kartenstapel der Projektliste.
    <tbody data-weitere="">
      <tr>
        <td colSpan={spalten} className="p-0">
          <Weitere
            gezeigt={fenster.gezeigt.length}
            gesamt={fenster.gesamt}
            href={href}
            wort={wort}
          />
        </td>
      </tr>
    </tbody>
  );
}
